import { useEffect, useRef } from "react";

type Options = {
  /**
   * - 'block': Prevents going back. Calls onBack when back is attempted.
   * - 'intercept': Consumes the back event to trigger onBack. Pushes state on mount, pops on back.
   *   If unmounted without back press, pops state manually.
   */
  behavior?: "block" | "intercept";
  enabled?: boolean;
  onBack?: () => void;
  /**
   * - true: (Default) Pops state if unmounted without back press (useful for drawers/modals).
   * - false: Does not pop state on unmount (useful for page-level navigation).
   */
  cleanupOnUnmount?: boolean;
};

// ── Nested-interceptor coordination ──────────────────────────────────────────
// Multiple intercept handlers can be mounted at once (e.g. the fullscreen story
// viewer opened from within a hiive/event drawer, which itself intercepts back).
// A single browser `popstate` fires EVERY listener, so without coordination the
// inner overlay's close would also pop the outer drawer. Two module-level guards
// fix this generally:
//   1. `interceptStack` — only the TOP-MOST intercept handler reacts to a real
//      back press; handlers below it ignore it.
//   2. `internalPopDeadline` — when a handler cleans itself up by calling
//      `history.back()` (closed programmatically, not via back button), it flags
//      the resulting popstate as internal so no other handler treats it as a
//      user back press.
//
// The flag is a short wall-clock window rather than a counter. A counter is
// wrong at both ends: the FIRST listener to see the popstate consumes it, so in
// the nested case the top-most handler then reads 0 and fires `onBack` on a pop
// it should have ignored; and when the unmounting handler was the last one
// mounted, nothing is left to decrement it, so the count leaks and silently
// swallows the user's next real back press. A deadline is ignored by every
// handler for the same event and expires on its own. `history.back()` delivers
// popstate on the next task (<1 frame), so the window only has to outlive a
// slow WebView frame — far below the ~300ms it takes a human to close something
// and then reach for Back.
const interceptStack: symbol[] = [];
export function hasBackNavigationInterceptor() {
  return interceptStack.length > 0;
}
let internalPopDeadline = 0;
const INTERNAL_POP_WINDOW_MS = 250;

// ── History-budget circuit breaker ───────────────────────────────────────────
// `pushedRef` stops this hook pushing twice for one live entry, and it survives
// an `<Activity>` hide/show because refs do. It does NOT survive a real
// remount — and a remount is exactly what happens, over and over, when Next's
// soft-retry loop re-creates a route's tree. Production shows the signature
// plainly: the same page image re-fetched seven times in a row while a loading
// skeleton is still on screen. Each of those cycles mounts a page-level
// interceptor, and each one pushes.
//
// That is not merely untidy history. WebKit throws after 100 state-object
// calls in 10 seconds, the budget is SHARED with `replaceState`, and Next's
// own router syncs history with `replaceState` on every navigation. Spending
// the budget here makes the router's own writes throw — so a retry loop that
// remounts this hook can stop the very navigation it is retrying from ever
// committing. The loop feeds itself.
//
// So: a hard ceiling on how much of that budget this hook may consume. It is
// deliberately a rate limit and not a smarter per-URL guard — a limit can only
// ever suppress the pathological repeats, never the first push for a screen,
// which keeps nested overlays and ordinary back-interception exactly as they
// were. `MAX` is set well under 100 to leave the rest for Next.
const PUSH_BUDGET_WINDOW_MS = 10_000;
const MAX_PUSHES_PER_WINDOW = 20;
let pushTimestamps: number[] = [];

function consumePushBudget(): boolean {
  const now = Date.now();
  pushTimestamps = pushTimestamps.filter(
    (at) => now - at < PUSH_BUDGET_WINDOW_MS,
  );
  if (pushTimestamps.length >= MAX_PUSHES_PER_WINDOW) return false;
  pushTimestamps.push(now);
  return true;
}

export function useBackNavigation({
  behavior = "block",
  enabled = true,
  onBack,
  cleanupOnUnmount = true,
}: Options = {}) {
  const pushedRef = useRef(false);
  const shouldCleanupRef = useRef(true);
  /** The URL our dummy entry was pushed for, to detect navigation on cleanup. */
  const pushedHrefRef = useRef<string | null>(null);

  // ⚠️ `onBack` and `cleanupOnUnmount` are read through refs and are
  // deliberately NOT effect dependencies. The effect pushes a dummy history
  // entry every time it runs, so any dependency that changes per render turns
  // this hook into a `history.pushState` loop — WebKit throws
  // "Attempt to use history.pushState() more than 100 times per 10 seconds"
  // after 100 state-object calls in 10s, and that budget is shared with
  // `replaceState`, so Next's own router sync starts throwing too.
  //
  // Every call site had an unstable handler: inline closures (global search
  // re-renders on each keystroke), `useCallback`s over changing deps, and
  // `useEffectEvent` — whose returned function is NOT referentially stable and
  // gets a fresh identity on every render. Refs give the "always latest
  // callback, never re-subscribe" semantics those call sites all wanted, so the
  // interceptor mounts exactly once per enabled window.
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;
  const cleanupOnUnmountRef = useRef(cleanupOnUnmount);
  cleanupOnUnmountRef.current = cleanupOnUnmount;

  useEffect(() => {
    if (!enabled) return;

    const token = Symbol("back-nav");
    interceptStack.push(token);

    // ⚠️ Push AT MOST ONCE per live dummy entry.
    //
    // Next 16 does not unmount the route you navigate away from — it hides it
    // in a React `<Activity>` boundary, and Activity *destroys effects on hide
    // and re-creates them on show*. This effect therefore re-runs every time
    // the user comes back to a route, not just on a real mount. Pushing
    // unconditionally meant every visit leaked another history entry for the
    // same URL, and because the page-level call sites pass
    // `cleanupOnUnmount: false`, nothing ever popped them.
    //
    // Each push is also a router action: Next patches `history.pushState` and
    // dispatches ACTION_RESTORE from it, so a leak here is a re-render storm,
    // not just untidy history. Production showed both
    // "Attempt to use history.pushState() more than 100 times per 10 seconds"
    // and its replaceState twin as live issues.
    //
    // `pushedRef` is the right guard precisely because Activity preserves
    // state and refs — only effects are torn down — so it still reads `true`
    // when the effect is re-created for a route we already pushed for. The
    // listener below is re-attached either way, which is what we actually
    // need from the re-run.
    if (!pushedRef.current && consumePushBudget()) {
      // Pass the CURRENT history state rather than `null`. Two reasons:
      // it preserves Next's `__NA` / `__PRIVATE_NEXTJS_INTERNALS_TREE` keys
      // verbatim, and because that state is already marked `__NA`, Next's
      // patch takes its internal fast path and skips the ACTION_RESTORE
      // dispatch — which is correct here, since this entry is the same URL
      // and the router has nothing to resync.
      history.pushState(window.history.state, "", window.location.href);
      pushedRef.current = true;
      pushedHrefRef.current = window.location.href;
    }

    const handlePopState = () => {
      // Ignore popstates produced by another interceptor's own cleanup back().
      if (Date.now() < internalPopDeadline) return;
      // Only the top-most mounted interceptor responds to a real back press.
      if (interceptStack[interceptStack.length - 1] !== token) return;

      if (behavior === "intercept") {
        // We consumed the back press (state popped)
        pushedRef.current = false;
        onBackRef.current?.();
      } else {
        // 'block' behavior
        onBackRef.current?.();
        // Re-push state to stay here. Same reasoning as the mount push:
        // carry the existing state so Next's internals survive and its patch
        // skips the router dispatch.
        //
        // Under the same budget: a 'block' interceptor facing a user (or a
        // loop) that keeps firing back is the other way this hook can exhaust
        // the history quota. Exceeding it means the next back press is not
        // blocked, which is a far smaller failure than breaking the router's
        // history writes for every route.
        if (consumePushBudget()) {
          history.pushState(window.history.state, "", window.location.href);
          pushedRef.current = true;
        }
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      const index = interceptStack.lastIndexOf(token);
      if (index !== -1) interceptStack.splice(index, 1);
      // Cleanup for intercept: if we pushed but never popped (manual close), pop
      // now — flagged as internal so an outer interceptor ignores the popstate.
      // Only pop when we are still ON the URL we pushed for.
      //
      // Activity runs this cleanup when a route is merely HIDDEN, not just
      // when it truly unmounts — so an overlay that was open while the user
      // navigated away would `history.back()` here and walk them backwards
      // out of the page they just opened. Comparing the href separates the
      // two cases without needing to know which one React is doing: if the
      // URL moved on, this teardown is a navigation, not a close.
      const stillOnPushedUrl =
        pushedHrefRef.current === null ||
        pushedHrefRef.current === window.location.href;

      if (
        behavior === "intercept" &&
        pushedRef.current &&
        shouldCleanupRef.current &&
        cleanupOnUnmountRef.current &&
        stillOnPushedUrl
      ) {
        internalPopDeadline = Date.now() + INTERNAL_POP_WINDOW_MS;
        history.back();
        pushedRef.current = false;
        pushedHrefRef.current = null;
      }
    };
  }, [enabled, behavior]);

  return {
    cancelCleanup: () => {
      shouldCleanupRef.current = false;
    },
  };
}
